/**
 * WebSocket utility for posttv Linux platform
 * Based on netgem websocket API implementation
 */

import { STORAGE_KEYS } from "./utils";

interface WebSocketMessage {
    type: string;
    data: {
        type?: string;
        method?: string;
        data?: {
            key?: string;
        };
        response?: any;
        request?: any;
    };
}

interface WebSocketCallbacks {
    onOpen?: () => void;
    onMessage?: (response: any) => void;
    onError?: (error: any) => void;
    onClose?: () => void;
}

class PostTvWebSocket {
    private ws: WebSocket | null = null;
    private url: string = "ws://localhost:6464/api";
    private readonly queryName: string = "localhost";
    private callbacks: WebSocketCallbacks = {};
    // FIFO queue — JSON.stringify-keyed Map was unreliable because STB echoes the
    // request back with different key order / extra fields, so lookups missed.
    // Netgem WebSocket processes commands sequentially, FIFO matches behaviour.
    private pendingCallbacks: Array<(response: any) => void> = [];

    /**
     * Netgem reference:
     * ws://localhost:6464/api?<signature_params>&q=localhost
     */
    private getWebSocketUrl(): string {
        const baseUrl = this.url;

        let signature = "";
        let signatureSource: "sessionStorage" | "location.hash" | null = null;
        try {
            signature = window.sessionStorage?.getItem(STORAGE_KEYS.posttvWsSignature) || "";
            if (signature) signatureSource = "sessionStorage";
        } catch (e) {
            signature = "";
        }

        // Fallback (less reliable): try current hash if we didn't capture early.
        if (!signature) {
            signature = window.location.hash || "";
            if (signature) signatureSource = "location.hash";
        }

        if (!signature) {
            console.log("[WebSocket] No WS signature found; using base URL");
            return baseUrl;
        }

        if (!this.looksLikeSignatureHash(signature)) {
            console.log("[WebSocket] Hash present but not a WS signature; using base URL");
            return baseUrl;
        }

        // Netgem reference: URI + "?" + signature.substr(1) + "&q=" + NAME
        // So we strip the leading "#" and append params directly
        let sigParams = signature.startsWith("#") ? signature.slice(1) : signature;
        // Remove leading "?" if present (shouldn't be, but defensive)
        sigParams = sigParams.startsWith("?") ? sigParams.slice(1) : sigParams;

        // If router-style hashes slipped through, don't use them.
        if (!sigParams || sigParams.startsWith("/") || !sigParams.includes("=")) {
            console.log("[WebSocket] WS signature rejected (invalid params); using base URL");
            return baseUrl;
        }

        // Build URL exactly like Netgem: baseUrl + "?" + sigParams + "&q=localhost"
        let url = `${baseUrl}?${sigParams}`;

        // Netgem adds q=localhost; keep it unless already provided.
        if (!/[?&]q=/.test(url)) {
            url += `&q=${this.queryName}`;
        }

        console.log(
            `[WebSocket] Using signed WS URL (source=${signatureSource ?? "unknown"}, sigLen=${signature.length})`
        );
        console.log("[WebSocket] sigParams preview:", sigParams.substring(0, 100) + "...");

        console.log("SOCKETS INIT HASH", window.sessionStorage?.getItem("initialHash"));
        return url;
    }

    private looksLikeSignatureHash(hash: string): boolean {
        if (!hash) return false;
        if (!hash.startsWith("#")) return false;
        if (hash.startsWith("#/")) return false; // router hash
        return hash.includes("=");
    }

    /**
     * Create WebSocket connection
     */
    create(callbacks: WebSocketCallbacks = {}): void {
        try {
            this.callbacks = callbacks;

            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                console.log("[WebSocket] Already connected");
                callbacks.onOpen?.();
                return;
            }

            const url = this.getWebSocketUrl();
            console.log("[WebSocket] Connecting to:", url);
            this.ws = new WebSocket(url);

            this.ws.onopen = (event) => {
                console.log("[WebSocket] Connection opened", event);
                callbacks.onOpen?.();
            };

            this.ws.onmessage = (msg) => {
                try {
                    const raw: WebSocketMessage = JSON.parse(msg.data);
                    const rawData = raw.data;
                    const response = rawData.response;
                    const request = rawData.request;

                    // JSON-stringify so STB-side success/error/value fields are
                    // visible in console (Chromium 55 console prints raw objects
                    // as "Object" with no detail).
                    try {
                        console.log(
                            "[WebSocket] Message received",
                            JSON.stringify({ response, request })
                        );
                    } catch (logErr) {
                        console.log("[WebSocket] Message received (raw)", response, request);
                    }

                    if (response) {
                        callbacks.onMessage?.(response);
                    }

                    // FIFO: pop the oldest pending callback and feed it the response.
                    const pending = this.pendingCallbacks.shift();
                    if (pending) {
                        try {
                            pending(response);
                        } catch (e) {
                            console.error("[WebSocket] Error in pending callback:", e);
                        }
                    }
                } catch (e) {
                    console.error("[WebSocket] Error parsing message:", e);
                    callbacks.onError?.(e);
                }
            };

            this.ws.onerror = (error) => {
                console.error("[WebSocket] Error:", error);
                callbacks.onError?.(error);
            };

            this.ws.onclose = (event) => {
                console.log("[WebSocket] Connection closed", event);
                callbacks.onClose?.();
                this.ws = null;
            };
        } catch (e) {
            console.error("[WebSocket] Error creating connection:", e);
            callbacks.onError?.(e);
        }
    }

    /**
     * Send command via WebSocket
     */
    sendCommand(args: any, callback?: (response: any) => void): void {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.error("[WebSocket] Cannot send command: WebSocket not open");
            return;
        }

        try {
            const request: WebSocketMessage = {
                type: "command",
                data: args
            };

            const msg = JSON.stringify(request);

            if (callback) {
                this.pendingCallbacks.push(callback);
            }

            console.log("[WebSocket] Sending command:", msg);
            this.ws.send(msg);
        } catch (e) {
            console.error("[WebSocket] Error sending command:", e);
            if (callback) {
                callback({ success: false, error: e });
            }
        }
    }

    /**
     * Get settings value
     */
    getSettings(key: string, callback?: (response: any) => void): void {
        const args = {
            type: "settings",
            method: "get",
            data: {
                key: key
            }
        };

        this.sendCommand(args, callback);
    }

    /**
     * Set settings value
     */
    setSettings(key: string, value: string, callback?: (response: any) => void): void {
        const args = {
            type: "settings",
            method: "set",
            data: {
                key: key,
                value: value
            }
        };

        this.sendCommand(args, callback);
    }

    /**
     * Check if WebSocket is connected
     */
    get isConnected(): boolean {
        return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
    }

    /**
     * Close WebSocket connection
     */
    close(): void {
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        this.pendingCallbacks = [];
    }
}

export const postTvWebSocket = new PostTvWebSocket();
